// Basic
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import Switch from '@/common-utils/components/ui/switch';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import ICONS from '@/common-utils/enums/icons';
import { nsPlan } from '@/app/i18n/namespaces';

/**
 * @param root
 * @param root.formData
 * @param root.setFormData
 * @param root.setIsValid
 * @param root.isLoading
 *@returns {JSX} PlanDetailsForm
 */
function PlanDetailsForm({ formData, setFormData, setIsValid, isLoading }) {

    // Constants
    const formSchema = z.object({
        productCode: z.string().min(1, { message: '' }),
        label: z.string().min(1, { message: '' }),
        autoActivate: z.boolean()
    });

    // Form definition
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: formData
    });

    // Hook
    useEffect(() => {
        setIsValid(form.formState.isValid);
        setFormData(form.getValues());
    }, [form.getValues(), form.formState.isValid]);

    return <Form {...form}>
        <form className='update-plan-form'>
            <FormField
                control={form.control}
                name='productCode'
                render={({ field }) => (
                    <FormItem>
                        <FormLabel required>
                            <Entity namespace={nsPlan} entity='keys.product_code' />
                        </FormLabel>
                        <FormControl>
                            <Input
                                type='text'
                                disabled={isLoading}
                                autoComplete='off'
                                {...field} />
                        </FormControl>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name='label'
                render={({ field }) => (
                    <FormItem>
                        <FormLabel required>
                            <Entity namespace={nsPlan} entity='keys.label' />
                        </FormLabel>
                        <FormControl>
                            <Input
                                type='text'
                                disabled={isLoading}
                                autoComplete='off'
                                {...field} />
                        </FormControl>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name='autoActivate'
                render={() => (
                    <FormItem>
                        <FormLabel>
                            <Entity namespace={nsPlan} entity='keys.auto_activate' />
                        </FormLabel>
                        <FormControl>
                            <Switch
                                name='autoActivate'
                                checked={form.getValues().autoActivate}
                                disabled={isLoading}
                                onCheckedChange={(value) => {
                                    form.setValue('autoActivate', value);
                                }}
                                entity={form.getValues().autoActivate ? 'modal.update_plan.auto_activate.yes' : 'modal.update_plan.auto_activate.no'}
                                namespace={nsPlan}
                            />
                        </FormControl>
                        {form.getValues().autoActivate &&
                            <div className='mt-4'>
                                <Icon icon={ICONS.WARNING} className={cn('text-warning mr-1')} />
                                <Entity entity='modal.update_plan.auto_activate.warning' namespace={nsPlan} className='text-sm font-normal' />
                            </div>
                        }
                    </FormItem>
                )
                }
            />
        </form>
    </Form>;
}

PlanDetailsForm.propTypes = {
    formData: PropTypes.object.isRequired,
    setFormData: PropTypes.func.isRequired,
    setIsValid: PropTypes.func.isRequired,
    isLoading: PropTypes.bool
};

export default PlanDetailsForm;